import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  DollarSign,
  Award,
  Heart,
  Plug,
  Settings,
  BarChart4,
  CalendarDays
} from "lucide-react";
import axios from "axios";

// Import Admin Section Components
import UsersSection from "../components/Admin/UsersSection";
import BudgetsSection from "../components/Admin/BudgetsSection";
import NominationsSection from "../components/Admin/NominationsSection";
import RecognitionTypesSection from "../components/Admin/RecognitionTypesSection";
import IntegrationsSection from "../components/Admin/IntegrationsSection";
import SettingsSection from "../components/Admin/SettingsSection";
import AnalyticsSection from "../components/Admin/AnalyticsSection";
import ImportantDatesSection from "../components/Admin/ImportantDatesSection";

const API_URL = process.env.REACT_APP_API_URL || "";

// Each tab gets its own color
const tabs = [
  { id: "users",        label: "Users",        icon: Users,     activeClass: "bg-blue-500 hover:bg-blue-600",       iconClass: "text-blue-500" },
  { id: "budgets",      label: "Budgets",      icon: DollarSign,activeClass: "bg-emerald-500 hover:bg-emerald-600", iconClass: "text-emerald-500" },
  { id: "nominations",  label: "Nominations",  icon: Award,     activeClass: "bg-amber-500 hover:bg-amber-600",     iconClass: "text-amber-500" },
  { id: "recognition-types", label: "Rec. Types", icon: Heart, activeClass: "bg-pink-500 hover:bg-pink-600", iconClass: "text-pink-500" },
  { id: "important-dates", label: "Dates", icon: CalendarDays, activeClass: "bg-rose-500 hover:bg-rose-600", iconClass: "text-rose-500" },
  { id: "analytics",    label: "Analytics",    icon: BarChart4, activeClass: "bg-indigo-500 hover:bg-indigo-600",   iconClass: "text-indigo-500" },
  { id: "integrations", label: "Integrations", icon: Plug,      activeClass: "bg-teal-500 hover:bg-teal-600",       iconClass: "text-teal-500" },
  { id: "settings",     label: "Settings",     icon: Settings,  activeClass: "bg-slate-500 hover:bg-slate-600",     iconClass: "text-slate-500" },
];

const USERS_PER_PAGE = 10;

const AdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab and page from URL or defaults
  const currentTab = searchParams.get("tab") || "users";
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const searchQuery = searchParams.get("search") || "";

  // Users state
  const [users, setUsers] = useState([]);
  const [usersCount, setUsersCount] = useState(0);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);

  // Fetch users when on users tab, page changes, or search changes
  useEffect(() => {
    if (currentTab === "users") {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, currentPage, searchQuery]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    setUsersError(null);
    try {
      const token = localStorage.getItem("token");
      const skip = (currentPage - 1) * USERS_PER_PAGE;
      const params = { skip, limit: USERS_PER_PAGE };
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await axios.get(`${API_URL}/api/v1/users/`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.data || []);
      setUsersCount(response.data.count || 0);
    } catch (error) {
      setUsersError(error.response?.data?.detail || "Failed to load users");
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const totalPages = Math.ceil(usersCount / USERS_PER_PAGE);

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  const handlePageChange = (page) => {
    const params = { tab: currentTab, page: page.toString() };
    if (searchQuery) params.search = searchQuery;
    setSearchParams(params);
  };

  const handleSearchChange = (search) => {
    // Reset to page 1 when search changes
    const params = { tab: currentTab, page: "1" };
    if (search) params.search = search;
    setSearchParams(params);
  };

  // Render active section
  const renderSection = () => {
    switch (currentTab) {
      case "users":
        return (
          <UsersSection
            users={users}
            usersCount={usersCount}
            isLoading={isLoadingUsers}
            error={usersError}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onRefresh={fetchUsers}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
          />
        );
      case "budgets":
        return <BudgetsSection />;
      case "nominations":
        return <NominationsSection />;
      case "recognition-types":
        return <RecognitionTypesSection />;
      case "important-dates":
        return <ImportantDatesSection />;
      case "analytics":
        return <AnalyticsSection />;
      case "integrations":
        return <IntegrationsSection />;
      case "settings":
        return <SettingsSection />;
      default:
        return <UsersSection />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-3 sm:pt-8">
        {/* Tabs Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-lg hover:border-gray-300 p-1.5">
            <div className="flex flex-nowrap gap-1 sm:gap-1 overflow-x-auto touch-scroll">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = currentTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex flex-1 items-center justify-center gap-1.5 p-2 sm:px-2 sm:py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? `${tab.activeClass} text-white shadow-md`
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : tab.iconClass}`} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Content Area */}
        <div
          key={currentTab}
          className="tab-panel-in bg-white rounded-2xl shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-lg hover:border-gray-300"
        >
          {renderSection()}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
